import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { Eye, PenLine, Tag, Trash2 } from 'lucide-react'
import { AdasBox, AdasTypography, AdasTooltip, AdasPopper } from '@components/wrapper-components'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { getAllProductTags, updateProductTags, removeCustomItem } from '../../../api/api'
import { currencyFormatter } from '../../../utils/utils'
import { isNationalAgreementLine } from '../../../utils/itemHelpers'
import { Logger } from '../../../logger'

// Render a single item row
export const ItemRow = ({
  item,
  isCustomItem,
  disabled: disabledProp,
  isNational: isNationalQwi,
  onClickEdit,
  type,
  isCustomInvoice,
  customData,
  onSaveSuccess
}) => {
  const readOnlyUser = useIsReadOnly()
  const disabled = disabledProp || readOnlyUser
  const tagIconRef = useRef(null)
  const [tagOptions, setTagOptions] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [openTagFor, setOpenTagFor] = useState(null)

  const tagDropdownRef = useRef(null)
  const ccId = useCenterStore((store) => store.ccId)

  // National Agreement lines are read-only for every role: no delete, and the pencil becomes a
  // view (eye) icon that opens the line item as a read-only sheet. CAR-4051 — the calibration tag
  // stays editable (it only labels which calibration the line is), so the tag picker uses `disabled`.
  // CAR-4097: on an NA QWI, parts from the centre's own catalog stay editable.
  const isNational = !!isNationalQwi && isNationalAgreementLine(item, type)
  const isReadOnly = disabled || isNational

  // Close tag dropdown on click outside
  useEffect(() => {
    if (!openTagFor) return
    const handleClickOutside = (e) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target) && !tagIconRef.current?.contains(e.target)) {
        setOpenTagFor(null)
        setAnchorEl(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openTagFor])

  const handleTagClick = useCallback(async () => {
    const isOpening = openTagFor !== item.id
    if (!isOpening) {
      setOpenTagFor(null)
      return
    }

    try {
      const response = await getAllProductTags({ ccid: ccId })
      if (response.status === 200 && Array.isArray(response.data)) {
        setTagOptions(response.data)
        setAnchorEl(tagIconRef.current)
        setOpenTagFor(item.id)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ItemRow > getAllProductTags' } })
    }
  }, [ccId, openTagFor, item.id])

  const handleTagSelection = async (itemId, tag) => {
    try {
      const args = [itemId, tag.id, type, customData?.id].filter(Boolean)
      await updateProductTags(...args)
      item.tag = tag
      setOpenTagFor(null)
      setAnchorEl(null)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ItemRow > updateProductTags' } })
    }
  }

  const tagMenuItems = useMemo(
    () =>
      tagOptions.map((tag) => ({
        label: tag.name,
        onClick: () => handleTagSelection(item.id, tag)
      })),
    [tagOptions, item.id]
  )

  const deleteCustomItemHandler = async () => {
    try {
      const res = await removeCustomItem({ type, id: customData.id, customItemId: item.id })
      if (res.status === 200 || res.status === 201) onSaveSuccess()
    } catch (error) {
      Logger.error({
        message: error,
        payload: { file: 'CustomItemForm/deleteCustomItemHandler', data: { type, id: item.id, customItemId: customData.id } }
      })
    }
  }

  const handleEditClick = () => {
    if (disabled) return
    onClickEdit?.({ type, product: item })
  }

  const isDiscount = item.type === 'DISCOUNT'
  // Calibration items carry a method/type ("Dynamic" / "Static") on the BE — the
  // exact field name varies by source, so we fall back through the common shapes
  // and append it to the bold title.
  const calibrationMethod = item.calibration_method || item.calibrationMethod || item.method || item.calType || ''
  const methodSuffix = type === 'calibration' && calibrationMethod ? ` (${calibrationMethod})` : ''
  const qtySuffix = item.quantity > 1 ? ` x ${item.quantity}` : ''
  // Lines mapped by ADAS Intelligence auto-quote carry source === 'AUTO_QUOTE'; everything
  // else (STANDARD defaults + tech-added) is MANUAL. Mark the auto ones with a trailing
  // primary-blue asterisk so techs can tell auto-selected lines from manual ones at a glance.
  const isAutoQuote = item.source === 'AUTO_QUOTE'
  const titleSuffix = `${methodSuffix}${qtySuffix}`

  return (
    <AdasBox className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-[#EBEDF0] py-3 last:border-b-0'>
      {/* Left — name + (method) + (tag) + tag-picker icon */}
      <AdasBox className='flex min-w-0 flex-1 items-center gap-2'>
        <AdasTooltip title={`${item.name}${titleSuffix}${isAutoQuote ? ' *' : ''}`}>
          <AdasTypography className='truncate text-[14px] text-[#0A1628]'>
            {item.name}
            {titleSuffix}
            {isAutoQuote && <span className='font-semibold text-[#1F6FEB]'> *</span>}
          </AdasTypography>
        </AdasTooltip>

        {type === 'calibration' && (
          <AdasBox
            className={`relative inline-flex items-center gap-1.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
            onClick={
              disabled
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    handleTagClick()
                  }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            {(item.tag?.name || !disabled) && (
              <AdasTypography className='text-[14px] text-[#6B7280]'>({item.tag?.name || 'Select Tag'})</AdasTypography>
            )}
            {!disabled && <Tag ref={tagIconRef} fill='currentColor' className='h-4 w-4 text-[#0A1628]' />}
            <AdasPopper open={openTagFor === item.id} anchorEl={anchorEl} placement='bottom-start'>
              <div ref={tagDropdownRef} className='py-1 min-w-[150px] rounded-md border border-[#D1D5D8] bg-white shadow-lg'>
                {tagMenuItems.map((tagItem, i) => (
                  <button
                    key={i}
                    onClick={() => tagItem.onClick()}
                    className='block w-full px-4 py-2 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                  >
                    {tagItem.label}
                  </button>
                ))}
              </div>
            </AdasPopper>
          </AdasBox>
        )}
      </AdasBox>

      {/* Optional unit-price column for custom invoices */}
      {isCustomInvoice && (
        <AdasBox className='w-[80px] text-right text-[13px] text-[#475569]'>{currencyFormatter(item.cost_price)}</AdasBox>
      )}

      {/* Right — total + edit icon */}
      <AdasBox className='inline-flex items-center gap-2'>
        <AdasTypography className={`min-w-[80px] text-right text-[14px] ${isDiscount ? 'text-destructive' : 'text-[#0A1628]'}`}>
          {currencyFormatter(item.total_price)}
        </AdasTypography>
        {isCustomItem &&
          !disabled &&
          (isNational ? (
            <Eye
              className='h-4 w-4 cursor-pointer text-[#475569] hover:text-[#0A1628]'
              onClick={handleEditClick}
              aria-label='View line item'
            />
          ) : (
            <PenLine className='h-4 w-4 cursor-pointer text-[#475569] hover:text-[#0A1628]' onClick={handleEditClick} />
          ))}
        {item.is_client_rebate_applied && !isReadOnly && (
          <Trash2 className='h-4 w-4 cursor-pointer text-destructive' onClick={deleteCustomItemHandler} />
        )}
      </AdasBox>
    </AdasBox>
  )
}

// Render list of items (no column header — section header strip lives in ExpandableSection)
export const ItemList = ({ itemList, name, isCustomItem = true, disabled, isNational, onClickEdit, type, customData, onSaveSuccess }) => {
  return (
    <AdasBox>
      {itemList?.length > 0 ? (
        <AdasBox>
          {itemList.map((item, index) => (
            <ItemRow
              key={index}
              item={item}
              isCustomItem={isCustomItem}
              disabled={disabled}
              isNational={isNational}
              onClickEdit={onClickEdit}
              type={type}
              customData={customData}
              onSaveSuccess={onSaveSuccess}
            />
          ))}
          {itemList
            .filter((item) => item.is_client_rebate_applied)
            .map((item, index) => (
              <AdasTypography key={`rebate-${index}`} variant='caption' className='block italic text-[#6B7280]'>
                Rebate/Discount: {item.client_rebate_percent ?? 0}%
              </AdasTypography>
            ))}
        </AdasBox>
      ) : (
        <AdasBox className='flex h-[80px] items-center justify-center rounded-sm border border-dashed border-[#D1D5D8]'>
          <AdasTypography className='text-[13px] text-[#6B7280]'>No {name?.toLowerCase() || 'items'} added yet</AdasTypography>
        </AdasBox>
      )}
    </AdasBox>
  )
}
